import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

/**
 * Tipos espelhando `PayableResponseDto`, campo a campo.
 *
 * A versão anterior declarava os campos em português (`fornecedorNome`,
 * `valor`, `dataVencimento`, `descricao`, `diasAtraso`, `juros`, `multa`…) e o
 * backend nunca mandou nenhum deles: a tela mostrava fornecedor em branco,
 * valor vazio e data inválida com dados reais no banco. Criar e baixar título
 * também falhavam, porque o corpo enviado não tinha os campos exigidos.
 */

export type PayableStatus =
  | 'OPEN'
  | 'PARTIALLY_SETTLED'
  | 'SETTLED'
  | 'OVERDUE'
  | 'CANCELLED'
  | 'RENEGOTIATED';

export type PayableOrigin =
  | 'PURCHASE'
  | 'EXPENSE'
  | 'SALARY'
  | 'TAX'
  | 'UTILITY'
  | 'RENT'
  | 'OTHER';

export interface Payable {
  id: string;
  supplierId: string;
  supplierName: string;
  documentNumber: string;
  documentDate: string;
  dueDate: string;
  originalAmount: number;
  /** Saldo em aberto: é o valor que a tela cobra. */
  openBalance: number;
  paidAmount: number;
  status: PayableStatus;
  category: string;
  costCenterId?: string;
  origin: PayableOrigin;
  notes?: string | null;
  paymentMethod?: string | null;
  interestAmount: number;
  fineAmount: number;
  discountAmount: number;
  paidAt?: string | null;
  installmentPlanId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PayableInput {
  supplierId: string;
  supplierName: string;
  documentNumber: string;
  documentDate: string;
  dueDate: string;
  originalAmount: number;
  category: string;
  costCenterId?: string;
  origin?: PayableOrigin;
  notes?: string;
}

/** `SettlePayableDto`: método e conta são obrigatórios — a baixa move caixa. */
export interface SettleInput {
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  accountId: string;
  notes?: string;
}

export interface RenegotiateInput {
  installments: { amount: number; dueDate: string }[];
  reason: string;
}

export interface SettlementResult {
  transactionId: string;
  entityId: string;
  newBalance: number;
  newStatus: string;
  settledAmount: number;
}

/**
 * Espelha `AgingReport` do domínio, que é o que o endpoint serializa.
 *
 * O DTO publicado dizia `buckets[]` e o handler devolvia o relatório de
 * domínio com um cast: três vocabulários para a mesma coisa, e os cartões de
 * faixa de vencimento vazios na tela. O DTO foi corrigido junto com isto.
 */
export interface AgingItem {
  id: string;
  documentNumber: string;
  documentDate: string;
  dueDate: string;
  daysOverdue: number;
  originalAmount: number;
  openBalance: number;
  partyName: string;
  category: string;
}

export interface AgingReport {
  current: number;
  days30: number;
  days60: number;
  days90: number;
  days120plus: number;
  total: number;
  items: AgingItem[];
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** Dias de atraso na data de hoje. Derivado — o backend não manda o número. */
export function daysOverdue(payable: Pick<Payable, 'dueDate' | 'status'>): number {
  if (payable.status === 'SETTLED' || payable.status === 'CANCELLED') return 0;
  const due = new Date(payable.dueDate);
  if (Number.isNaN(due.getTime())) return 0;
  const today = new Date();
  const diff = Math.floor((today.getTime() - due.getTime()) / 86_400_000);
  return diff > 0 ? diff : 0;
}

export function usePayables(params?: {
  status?: string;
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
}) {
  return useQuery({
    queryKey: [...queryKeys.finance.payables(), params],
    queryFn: () =>
      apiClient.get<PaginatedResponse<Payable>>('/finance/payables', {
        params: {
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 50),
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.startDate ? { startDate: params.startDate } : {}),
          ...(params?.endDate ? { endDate: params.endDate } : {}),
        },
      }),
  });
}

export function usePayablesAging() {
  return useQuery({
    queryKey: [...queryKeys.finance.payables(), 'aging'],
    queryFn: () => apiClient.get<AgingReport>('/finance/payables/aging'),
  });
}

export function useCreatePayable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PayableInput) =>
      apiClient.post<Payable>('/finance/payables', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.finance.payables() });
    },
  });
}

export function useUpdatePayable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<PayableInput> & { id: string }) =>
      apiClient.put<Payable>(`/finance/payables/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.finance.payables() });
    },
  });
}

export function useSettlePayable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: SettleInput & { id: string }) =>
      apiClient.post<SettlementResult>(`/finance/payables/${id}/settle`, {
        payableId: id,
        ...input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.finance.payables() });
    },
  });
}

export function useRenegotiatePayable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: RenegotiateInput & { id: string }) =>
      apiClient.post<{ originalPayable: Payable; installments: Payable[] }>(
        `/finance/payables/${id}/renegotiate`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.finance.payables() });
    },
  });
}

export function useDeletePayable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/finance/payables/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.finance.payables() });
    },
  });
}
